import type { Checkpoint } from './types.js';

/**
 * How the car clears a gate.
 *
 * Intermediate checkpoints are radius gates — get within `radius` of the centre and it
 * counts. That is deliberately generous: they exist to stop you cutting across town, not
 * to be threaded, and having a gate you demonstrably drove through fail to register at
 * 120 km/h is much worse than the occasional early trigger nobody notices.
 *
 * The finish is the one place where that trade is wrong. A finish radius scales with the
 * road, up to 30 m, so the radius test declares you the winner while you are still most
 * of a city block short of the line — you see the banner, and *then* you arrive. So the
 * finish is a real line: the car has to cross the plane of the gate, going forwards.
 */

/** Signed distance in front of the gate's plane. Negative is still approaching. */
function ahead(gate: Checkpoint, x: number, z: number): number {
  return (x - gate.x) * gate.dx + (z - gate.z) * gate.dz;
}

/** Distance to the left of the gate's centre line. */
function lateral(gate: Checkpoint, x: number, z: number): number {
  return (x - gate.x) * -gate.dz + (z - gate.z) * gate.dx;
}

export function withinRadius(gate: Checkpoint, x: number, z: number): boolean {
  const dx = x - gate.x;
  const dz = z - gate.z;
  return dx * dx + dz * dz <= gate.radius * gate.radius;
}

/**
 * Longest step `passedThrough` treats as driven, metres. Top speed is about 45 m/s, so a
 * 50 ms report covers a little over 2; this leaves room for a report or two going missing.
 */
const MAX_SWEEP_M = 30;

/**
 * True when any point of the segment from (ax,az) to (bx,bz) comes within the gate's radius.
 *
 * The radius test, applied to the path between two positions rather than to where the car
 * ended up. The server only sees a position every 50 ms, and a point test there passes a
 * gate the car clipped the edge of only if a report happened to land inside it — so how
 * often a client reported decided, a little, whether a checkpoint counted.
 */
export function passedThrough(
  gate: Checkpoint,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): boolean {
  const sx = bx - ax;
  const sz = bz - az;
  const len2 = sx * sx + sz * sz;
  // Further than any car drives between two reports — a respawn, a rejoin, a first report
  // from a car the server last saw on the grid. That is a jump, not a path, and sweeping a
  // gate across it would hand out a checkpoint the car never went near.
  if (len2 > MAX_SWEEP_M * MAX_SWEEP_M) return withinRadius(gate, bx, bz);
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((gate.x - ax) * sx + (gate.z - az) * sz) / len2)) : 0;
  return withinRadius(gate, ax + sx * t, az + sz * t);
}

/**
 * How far along the segment from (ax,az) to (bx,bz) the car met the finish, 0 to 1, for a
 * segment `crossedFinish` accepted.
 *
 * What lets the server time a finish to better than the gap between two reports: the
 * crossing happened this far between the moments the two positions were true. The
 * fallback clause in `crossedFinish` has no crossing to point at and answers 1.
 */
export function finishFraction(
  gate: Checkpoint,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const fa = ahead(gate, ax, az);
  const fb = ahead(gate, bx, bz);
  if (fa > 0 || fb <= 0 || fa === fb) return 1;
  return Math.max(0, Math.min(1, fa / (fa - fb)));
}

/**
 * True when the segment from (ax,az) to (bx,bz) passes through the gate's mouth in the
 * direction of travel.
 *
 * It is a segment rather than a point test because the server only sees positions at
 * 20 Hz, which at 150 km/h is 2 m apart — a point test on either end would miss the line
 * entirely often enough to matter.
 */
export function crossedLine(
  gate: Checkpoint,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): boolean {
  const fa = ahead(gate, ax, az);
  const fb = ahead(gate, bx, bz);
  // Behind at the start, in front at the end. Anything else is approaching, leaving, or
  // going backwards through it.
  if (fa > 0 || fb <= 0) return false;
  const t = fa === fb ? 0 : fa / (fa - fb);
  const cx = ax + (bx - ax) * t;
  const cz = az + (bz - az) * t;
  return Math.abs(lateral(gate, cx, cz)) <= gate.radius;
}

/**
 * The finish test used by both the client's optimistic copy and the server's
 * authoritative one.
 *
 * The line crossing is the real answer. The second clause catches a car that ends up
 * inside the gate and past the line without a clean crossing between two samples — a
 * respawn, a wall bounce that spins it through sideways — and still cannot fire early,
 * because it needs the car to be genuinely beyond the plane.
 */
export function crossedFinish(
  gate: Checkpoint,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): boolean {
  if (crossedLine(gate, ax, az, bx, bz)) return true;
  return ahead(gate, bx, bz) > 0 && withinRadius(gate, bx, bz);
}
